import { Link } from 'react-router-dom';
import { Truck, Wrench, Zap, Cloud, HeartPulse, Plug, LayoutDashboard, FileText } from 'lucide-react';
import './EcosystemPage.css';

const partners = [
  {
    Icon: Truck, category: 'EV Manufacturers', title: 'Vehicle Partners',
    desc: 'We source our fleet from leading EV manufacturers who specialize in commercial and logistics vehicles. Our vehicle partners provide trucks, bikes, and larger commercial vehicles purpose-built for demanding delivery operations.',
    items: ['EV Trucks', 'EV Bikes', 'Commercial Vehicles'],
  },
  {
    Icon: Wrench, category: 'Fleet Servicing', title: 'Maintenance Partners',
    desc: 'Reliable service providers such as B-Quik ensure our fleet stays in peak condition. Scheduled maintenance, emergency repairs, and proactive diagnostics keep every vehicle road-ready at all times.',
    items: ['Scheduled Maintenance', 'Emergency Repair', 'Remote Diagnostics'],
  },
  {
    Icon: Zap, category: 'Energy Network', title: 'Charging Infrastructure',
    desc: 'We partner with nationwide EV charging networks and strategic rest-stop locations to ensure our fleet can operate continuously across urban and regional routes without range concerns.',
    items: ['Fast Charging', 'Rest-Stop Network', 'Grid Integration'],
  },
  {
    Icon: Cloud, category: 'Cloud, AI & IoT', title: 'Technology Partners',
    desc: 'Our platform is powered by best-in-class cloud infrastructure, AI analytics, and IoT hardware partners. These integrations drive our real-time dashboards, predictive maintenance, and compliance systems.',
    items: ['Cloud Platforms', 'AI Analytics', 'IoT Hardware'],
  },
  {
    Icon: HeartPulse, category: 'Healthcare & Beyond', title: 'Industry Partners',
    desc: 'Our primary clients include hospitals, pharmaceutical distributors, and medical supply companies. As we scale, we expand into food distribution and FMCG supply chains.',
    items: ['Hospitals', 'Pharma Distributors', 'Food & FMCG'],
  },
];

const ecoSteps = [
  { Icon: Plug,            num:'01', title:'Connect',    desc:'Partners integrate with the Axion platform via API or our control tower dashboard.' },
  { Icon: LayoutDashboard, num:'02', title:'Coordinate', desc:'Fleet, routes, cargo conditions, and compliance are managed centrally in real time.' },
  { Icon: Truck,           num:'03', title:'Deliver',    desc:'EV vehicles execute routes with full sensor monitoring, GPS tracking, and cold-chain compliance.' },
  { Icon: FileText,        num:'04', title:'Report',     desc:'Partners receive automated delivery reports, temperature logs, and GDP compliance certificates.' },
];

const specs = [
  {
    category: 'Performance',
    rows: [
      ['Max Speed', '120 km/h'],
      ['Range (Full Load)', '250 km'],
      ['Acceleration (0–80 km/h)', '8.5 s'],
      ['Payload Capacity', '800 kg'],
      ['Towing Capacity', '1,200 kg'],
    ],
  },
  {
    category: 'Battery & Power',
    rows: [
      ['Battery Capacity', '80 kWh'],
      ['Battery Type', 'LiFePO4 (Lithium Iron Phosphate)'],
      ['DC Fast Charge (20–80%)', '45 min'],
      ['AC Charge Time', '6–8 hrs'],
      ['Regenerative Braking', 'Yes'],
    ],
  },
  {
    category: 'Cold Chain System',
    rows: [
      ['Temperature Range', '−25°C to +25°C'],
      ['Cooling Zones', '2 Independent Zones'],
      ['Humidity Control', '30%–90% RH'],
      ['Temperature Logging', 'Real-Time, Cloud-Synced'],
      ['GDP / GDPMD Compliant', 'Yes'],
    ],
  },
  {
    category: 'Smart Technology',
    rows: [
      ['Connectivity', '4G LTE + WiFi'],
      ['OTA Updates', 'Supported'],
      ['Telematics Platform', 'Axion Cloud Dashboard'],
      ['Driver Behavior Monitoring', 'AI-Powered'],
      ['Alcohol Sensor Detection', 'Integrated'],
    ],
  },
  {
    category: 'Safety & Surveillance',
    rows: [
      ['AI CCTV Cameras', '4-Channel (360° Coverage)'],
      ['Dashcam', 'Front + Rear, 1080p'],
      ['Cargo Monitoring', 'Interior AI Camera'],
      ['Emergency Braking', 'Automatic'],
      ['Lane Departure Warning', 'Active'],
    ],
  },
  {
    category: 'Tracking & Compliance',
    rows: [
      ['GPS Tracking', 'Real-Time, ±1.5 m Accuracy'],
      ['Geo-Fencing', 'Configurable Zones'],
      ['Route Optimization', 'AI-Assisted'],
      ['Delivery ETA', 'Live Customer Visibility'],
      ['ISO Compliance', 'ISO 13485 / ISO 9001'],
    ],
  },
  {
    category: 'Dimensions & Build',
    rows: [
      ['Vehicle Length', '5,200 mm'],
      ['Vehicle Width', '1,960 mm'],
      ['Vehicle Height', '2,050 mm'],
      ['Cargo Volume', '8.5 m³'],
      ['Kerb Weight', '2,350 kg'],
    ],
  },
  {
    category: 'Operational',
    rows: [
      ['Operating Hours', '24/7 Fleet Ready'],
      ['Maintenance Interval', 'Every 20,000 km'],
      ['Warranty', '3 Years / 150,000 km'],
      ['Fleet Management', 'Axion Control Tower'],
      ['Remote Diagnostics', 'Over-the-Air'],
    ],
  },
];

const flowNodes = ['Vehicle Partners', 'Axion Platform', 'Maintenance', 'Charging Infra', 'Industry Clients'];

export default function EcosystemPage() {
  return (
    <div className="eco-page">

      {/* ── HERO ── */}
      <section className="ep-hero">
        <div className="ep-hero-grid" />
        <div className="ep-hero-content">
          <span className="ep-eyebrow">The Axion Ecosystem</span>
          <h1 className="ep-title">One Network,<br /><span className="ep-accent">Many Partners</span></h1>
          <p className="ep-desc">
            Axion Mobility operates as a platform ecosystem, connecting vehicle manufacturers,
            maintenance providers, charging networks, technology partners, and industry clients
            into a single, seamlessly coordinated EV logistics network.
          </p>
          <div className="ep-flow">
            {flowNodes.map((n, i) => (
              <div key={n} className="ep-flow-wrap">
                <div className={`ep-flow-node ${n === 'Axion Platform' ? 'ep-flow-node--center' : ''}`}>{n}</div>
                {i < flowNodes.length - 1 && <div className="ep-flow-arrow">→</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PARTNER DETAILS ── */}
      <section className="ep-partners">
        <div className="ep-section-header">
          <span className="ep-eyebrow">Our Ecosystem Partners</span>
          <h2>Each partner plays a vital role in making Axion's fully connected EV logistics network possible.</h2>
        </div>
        <div className="ep-partner-list">
          {partners.map(({ Icon, category, title, desc, items }) => (
            <div key={title} className="ep-partner">
              <div className="ep-partner-left">
                <p className="ep-partner-category">{category}</p>
                <div className="ep-partner-icon"><Icon size={28} strokeWidth={1.6} /></div>
              </div>
              <div className="ep-partner-right">
                <h3>{title}</h3>
                <p>{desc}</p>
                <div className="ep-partner-tags">
                  {items.map(it => <span key={it}>{it}</span>)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="ep-how">
        <span className="ep-eyebrow">How the Ecosystem Works</span>
        <h2>Every partner connects through the Axion platform, creating a unified, transparent, and reliable flow of operations.</h2>
        <div className="ep-how-grid">
          {ecoSteps.map(({ Icon, num, title, desc }) => (
            <div key={num} className="ep-how-card">
              <div className="ep-how-num">{num}</div>
              <div className="ep-how-icon"><Icon size={24} strokeWidth={1.7} /></div>
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── VEHICLE SPECS ── */}
      <section className="ep-specs">
        <div className="ep-specs-header">
          <span className="ep-eyebrow">Phase 1 Fleet</span>
          <h2>EV Cold-Chain Pickup</h2>
          <p>Built for healthcare and pharmaceutical logistics. Zero-emission, fully traceable, and temperature-controlled.</p>
        </div>

        <div className="ep-specs-highlights">
          {[['80 kWh','Battery Capacity'],['250 km','Full-Load Range'],['−25°C','Min Temp Control'],['800 kg','Payload Capacity'],['4-CH AI','CCTV System'],['Real-Time','GPS Tracking']].map(([v,l]) => (
            <div key={l} className="ep-spec-hi">
              <strong>{v}</strong><span>{l}</span>
            </div>
          ))}
        </div>

        <div className="ep-specs-header" style={{marginTop:'56px'}}>
          <h3>Vehicle Specifications</h3>
          <p>Every Axion EV truck is engineered with integrated smart technology and cold-chain compliance built in from the ground up.</p>
        </div>

        <div className="ep-specs-grid">
          {specs.map(({ category, rows }) => (
            <div key={category} className="ep-spec-table">
              <h4 className="ep-spec-cat">{category}</h4>
              {rows.map(([label, value]) => (
                <div key={label} className="ep-spec-row">
                  <span className="ep-spec-label">{label}</span>
                  <span className="ep-spec-value">{value}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div style={{textAlign:'center',marginTop:'48px'}}>
          <Link to="/" className="ep-back">← Back to Overview</Link>
        </div>
      </section>

    </div>
  );
}
